<script>
import formatDateMixin from '@/mixins/formatDateMixin'
import { mapActions, mapGetters } from 'vuex'
import EmptyStateBlock from '@components/general/empty-state-block.vue'
import { isWorkingTowardsQual } from '@/helpers/candidate-details-helpers'

export default {
  name: 'CandidateQualifications',
  components: { EmptyStateBlock },
  mixins: [formatDateMixin],
  props: {
    qualifications: {
      type: Array,
      default: null,
    },
    timeZone: {
      type: String,
      default: null,
    },
  },
  data: () => {
    return {
      fileLoading: -1,
    }
  },
  computed: {
    ...mapGetters({
      isLoading: 'file/isLoading',
    }),
    hasQualifications() {
      return this.qualifications && this.qualifications.length > 0
    },
    formattedQualifications() {
      return this.qualifications.map((qual) => ({
        ...qual,
        institute: qual?.institute?.toLowerCase() || '',
        degree: qual?.degree?.toLowerCase() || '',
        completedUTC: this.getCompletedDate(qual, this.timeZone),
      }))
    },
  },
  methods: {
    ...mapActions({
      download: 'file/download',
    }),

    async downloadFile(fileId) {
      this.fileLoading = fileId

      await this.download(fileId)
    },
    getCompletedDate(qual, timeZone) {
      if (!qual) return ''

      if (isWorkingTowardsQual(qual))
        return this.$t('candidateDetailsPopup.qualification.inProgress')

      return qual.completedUTC
        ? this.renderDate(qual.completedUTC, timeZone)
        : ''
    },
  },
}
</script>

<template>
  <v-card flat>
    <EmptyStateBlock
      v-if="!hasQualifications"
      icon="mdi-school"
      :title="$t('candidateDetailsPopup.qualification.emptyState.title')"
    />
    <v-simple-table v-else>
      <template v-slot>
        <thead>
          <tr>
            <th class="text-left text-md-bold long-text-cell">{{
              $t('candidateDetailsPopup.qualification.headers.institute')
            }}</th>
            <th class="text-left text-md-bold long-text-cell">{{
              $t('candidateDetailsPopup.qualification.headers.degree')
            }}</th>
            <th class="text-left text-md-bold">{{
              $t('candidateDetailsPopup.qualification.headers.dateCompleted')
            }}</th>
            <th class="text-left text-md-bold">{{
              $t('candidateDetailsPopup.qualification.headers.teachingMethods')
            }}</th>
            <th class="text-left text-md-bold">{{
              $t('candidateDetailsPopup.qualification.headers.downloads')
            }}</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="{
              id,
              institute,
              degree,
              completedUTC,
              grades,
              files,
            } in formattedQualifications"
            :key="`candidateQualifications_quals_${id}`"
          >
            <td class="text-md text-capitalize institute-cell">{{
              institute
            }}</td>
            <td class="text-md text-capitalize degree-cell">{{ degree }}</td>
            <td
              class="text-md text-no-wrap"
              :class="{
                'font-italic':
                  completedUTC ===
                  $t('candidateDetailsPopup.qualification.inProgress'),
              }"
            >
              {{ completedUTC }}
            </td>
            <td>
              <v-chip-group v-if="grades && grades.length > 0">
                <v-chip
                  v-for="({ title = '' }, gi) in grades"
                  :key="`candidateQualifications_grades_${gi}`"
                  class="ma-1"
                  small
                >
                  {{ title }}
                </v-chip>
              </v-chip-group>
            </td>
            <td class="text-center">
              <v-col
                v-for="({ fileId = 0 }, fi) in files"
                :key="`qualificationFiles_files_${fileId}`"
                cols="1"
                class="pa-1"
              >
                <v-btn
                  x-small
                  text
                  color="primary"
                  :loading="isLoading && fileId === fileLoading"
                  @click="downloadFile(fileId)"
                >
                  <v-icon left dark>
                    mdi-cloud-download
                  </v-icon>
                  <span class="hidden-sm-and-down">File {{ fi + 1 }}</span>
                </v-btn>
              </v-col>
            </td>
          </tr>
        </tbody>
      </template>
    </v-simple-table>
  </v-card>
</template>

<style lang="scss" scoped>
.institute-cell {
  min-width: 140px;
}

.degree-cell {
  min-width: 180px;
}
</style>
